import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Heart } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useApp } from "@/context/AppContext";
import { track, Events } from "@/lib/analytics";
import { waLink } from "@/lib/constants";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/projects", label: "Projects" },
  { to: "/locations", label: "Locations" },
  { to: "/guides", label: "Guides" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { settings, shortlist } = useApp();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  const phone = settings?.phone || "9325271272";

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b transition-all duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-border shadow-sm" : "bg-white/60 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="ec-container flex items-center justify-between h-16 lg:h-20">
        <Link to="/" data-testid="logo" className="flex items-center gap-2.5" aria-label="Endeavour Castle Pune home">
          <span className="h-9 w-9 rounded-sm bg-primary text-primary-foreground grid place-items-center font-display text-lg">E</span>
          <span className="leading-tight">
            <span className="block font-display text-lg text-primary">Endeavour Castle</span>
            <span className="block text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Pune Real Estate</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink
              key={n.label}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors hover:text-primary ${isActive ? "text-primary" : "text-foreground/70"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/compare" data-testid="header-shortlist" aria-label="Shortlist" className="relative hidden sm:grid place-items-center h-10 w-10 rounded-sm hover:bg-muted transition-colors">
            <Heart className="h-5 w-5 text-primary" aria-hidden />
            {shortlist.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-accent text-accent-foreground text-[10px] grid place-items-center">{shortlist.length}</span>
            )}
          </Link>
          <a href={`tel:${phone}`} onClick={() => track(Events.PHONE, { where: "header" })}
            data-testid="header-call" aria-label="Call us" className="grid place-items-center h-10 w-10 rounded-sm hover:bg-muted transition-colors lg:hidden">
            <Phone className="h-5 w-5 text-primary" aria-hidden />
          </a>
          <Link to="/requirements" data-testid="header-cta"
            className="hidden sm:inline-flex items-center h-10 px-5 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
            Find My Property
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="menu-toggle" aria-label="Open menu" className="lg:hidden grid place-items-center h-10 w-10 rounded-sm hover:bg-muted transition-colors">
                <Menu className="h-6 w-6 text-primary" aria-hidden />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm bg-white p-0">
              <div className="flex items-center justify-between p-5 border-b border-border">
                <span className="font-display text-lg text-primary">Menu</span>
                <button onClick={() => setOpen(false)} aria-label="Close menu"><X className="h-5 w-5" aria-hidden /></button>
              </div>
              <nav className="flex flex-col p-3" aria-label="Mobile">
                {NAV.map((n) => (
                  <NavLink key={n.label} to={n.to} end={n.to === "/"} data-testid={`mnav-${n.label.toLowerCase()}`}
                    className={({ isActive }) => `px-4 py-3 rounded-sm text-base font-medium transition-colors ${isActive ? "bg-muted text-primary" : "text-foreground/80 hover:bg-muted"}`}>
                    {n.label}
                  </NavLink>
                ))}
                <Link to="/compare" className="px-4 py-3 rounded-sm text-base font-medium text-foreground/80 hover:bg-muted">Shortlist & Compare</Link>
              </nav>
              <div className="p-4 mt-auto grid grid-cols-2 gap-3 border-t border-border">
                <a href={`tel:${phone}`} onClick={() => track(Events.PHONE, { where: "menu" })}
                  className="flex items-center justify-center gap-2 h-11 rounded-sm border border-primary text-primary text-sm font-semibold">
                  <Phone className="h-4 w-4" aria-hidden /> Call
                </a>
                <a href={waLink(settings?.whatsapp || phone, "Hi, I'm looking for a property in Pune.")} target="_blank" rel="noreferrer"
                  onClick={() => track(Events.WHATSAPP, { where: "menu" })}
                  className="flex items-center justify-center gap-2 h-11 rounded-sm bg-[#25D366] text-white text-sm font-semibold">
                  WhatsApp
                </a>
              </div>
              <div className="p-4">
                <Link to="/requirements" className="flex items-center justify-center h-12 rounded-sm bg-primary text-primary-foreground text-sm font-semibold">
                  Find My Property
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
